import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { MARKET_CATEGORY_TREE, marketCategoryLabel, SHOW_CATEGORY_COUNTS } from '../lib/marketCategories';
import { fetchMarketCards, fetchShowcase, searchMarket } from '../lib/marketCatalog';
import { recentIds } from '../lib/marketLocal';
import { SERVICE_ROUTES } from '../lib/marketServices';
import { fetchMarketState } from '../lib/marketMode';
import { exclusivesPublic } from '../lib/marketExclusives';
import MarketShell, { SearchBox, SHELL_T, Thumb } from '../components/market/MarketShell';
import { useDeliveredConfig } from '../lib/marketMode';
import { MIX, PICKS_HOME, PICKS_MIN, categoryPicks, dayTabs, mixPicks } from '../lib/homePicks';
import { track } from '../lib/growthTracker';
import { IMAGE_SEARCH } from '../lib/featureFlags';
import { ACCEPTED, prepareSearchImage, stashSearchImage } from '../lib/marketImageSearch';
import MarketProductCard from '../components/market/MarketProductCard';

// The marketplace home (/market, and "/" once the marketplace is the home page
// — see components/market/MarketHomeGate).
// Layout (owner, 2026-10-29), top to bottom: search · photo search card ·
// the three services as coloured tiles · the delivered-price banner · three
// trust tiles · categories as an icon grid · product rows · the three
// services as full cards. No 299 fee here (it lives in each service's flow),
// no seasonal content, no «جوي/بحري» (checkout only).
// Earlier layout, kept for the exports other pages still use: a short hero
// (categories · one headline · a mosaic of real listed products), MAABAR's
// three services as one slim strip, category cards made of real product
// photos, the curated collections (market_merchandising.sql — ranked on real
// data, staff pins first, stable between refreshes; each product once per
// page), popular subcategories, picks from what this visitor viewed,
// suppliers in the market, recently viewed.
// Fees, refunds and shipping are explained where they apply (checkout), not
// on the first screen. Everything is live catalog data; no promotion,
// discount or popularity figure is shown that the data does not carry.

export const HOME_T = {
  ar: {
    headline: 'منتجات الجملة من الصين إلى بابك',
    sub: 'تصفح المنتجات بأسعار الجملة، ومعبر تتولى الشراء والشحن.',
    browse: 'تصفّح الأقسام', services: 'خدمات معبر', notFound: 'لم تجد ما تبحث عنه؟',
    // Service names exactly as on the home page and in the trader's menus.
    managed: ['الطلب المُدار', 'نبحث ونتفاوض ونتابع عنك'], rfq: ['اطلب عرض سعر', 'تصلك عروض المصانع مباشرة'], idea: ['صنّع فكرتك', 'منتج بتصميمك أو علامتك'],
    // The managed-order card under the hero — the owner's own words (2026-10-21).
    hire: ['تحتاج توكل أحد بطلبك؟', 'وكّل معبر بطلبك'],
    // «حصريات معبر» (owner, 2026-10-29): its own page, reached from one card.
    excl: ['حصريات معبر', 'منتجات يختارها فريق معبر — تواصل وتفاوض على السعر والشحن'], exclHidden: 'مخفي عن العملاء',
    byCat: 'تسوّق حسب القسم', byCatSub: (n) => `${n} قسمًا بمنتجات متاحة الآن`, products: (n) => `${n} منتج`, allCats: 'كل المنتجات',
    popular: 'أقسام رائجة', popularSub: 'الأكثر منتجات في السوق الآن',
    forYou: 'مقترحات لك', forYouSub: 'من القسم الذي شاهدته مؤخرًا', recent: 'شاهدتها مؤخرًا',
    sellers: 'موردون في السوق', sellersSub: 'التقييم من سجل المورد نفسه', nProducts: (n) => `${n} منتج في السوق`,
    photo: ['صوّر المنتج، ونلقاه لك من المصنع', 'شفت منتج في محل أو إعلان؟ ارفع صورته'],
    svcHead: 'ما لقيت منتجك؟ عندنا 3 خدمات تجيبه لك',
    tiles: { rfq: ['اطلب عرض سعر', 'المصانع تسعّر لك خلال 48 ساعة'], managed: ['الطلب المُدار', 'فريقنا يسوي كل شي عنك'], idea: ['صنّع فكرتك', 'منتج باسمك وبراندك'] },
    banner: { badge: 'السعر اللي تشوفه هو اللي تدفعه', title: 'بضاعة المصانع بسعر الجملة، والسعر النهائي مع الشحن والجمارك يطلع لك في السلة قبل الدفع',
      text: 'آلاف المنتجات بأسعار المصانع، شامل الشحن والجمارك، بدون أي مفاجآت', cta: 'تصفّح المنتجات' },
    trust: ['أسعار المصنع مباشرة', 'السعر النهائي مع الشحن والجمارك في سلتك قبل الدفع', 'دفع آمن وتتبع للطلب'],
    bottomHead: 'ما لقيت منتجك؟ نجيبه لك من المصنع',
    cards: {
      rfq: { title: 'اطلب عرض سعر', tag: 'مجاني · عروض المصانع خلال 48 ساعة', text: 'اكتب وش تبي، والمصانع ترسل لك عروضها، وتختار الأفضل', note: 'يناسبك لو: تعرف منتجك وتبي أفضل سعر' },
      managed: { title: 'الطلب المُدار', tag: 'الأسهل لو أول مرة تستورد', text: 'فريقنا يتولى كل شي عنك: يدوّر المصنع، يفاوض، يفحص البضاعة، ويشحنها لك', note: 'يناسبك لو: أول مرة تستورد، أو ما عندك وقت' },
      idea: { title: 'صنّع فكرتك', tag: 'منتج باسمك وبراندك', text: 'عندك فكرة منتج أو تبي شعارك عليه؟ نلقى لك المصنع اللي يصنّعه', note: 'يناسبك لو: تبي تبني براند خاص فيك' },
    },
    seeAll: 'عرض الكل', closed: 'السوق غير متاح بعد.', previewDenied: 'حسابك مشرف، لكن قاعدة بيانات السوق التي يتصل بها هذا التطبيق لا تعرفه مشرفًا، فلا تعرض له المنتجات غير المنشورة.',
  },
  en: {
    headline: 'Wholesale products from China to your door',
    sub: 'Browse products at wholesale prices — MAABAR handles purchasing and shipping.',
    browse: 'Browse categories', services: 'MAABAR services', notFound: 'Can\'t find it?',
    managed: ['Managed order', 'We source, negotiate and follow up'], rfq: ['Request a quote', 'Factory offers come straight to you'], idea: ['Make your idea', 'A product in your design or brand'],
    hire: ['Need someone to handle your order?', 'Let MAABAR handle it'],
    excl: ['MAABAR Exclusives', 'Products picked by the MAABAR team — talk and negotiate price and shipping'], exclHidden: 'Hidden from customers',
    byCat: 'Shop by category', byCatSub: (n) => `${n} categories with products available now`, products: (n) => `${n} products`, allCats: 'All products',
    popular: 'Popular subcategories', popularSub: 'With the most products in the market now',
    forYou: 'Picked for you', forYouSub: 'From the category you viewed recently', recent: 'Recently viewed',
    sellers: 'Suppliers in the market', sellersSub: 'Ratings come from the supplier\'s own record', nProducts: (n) => `${n} products in the market`,
    photo: ['Snap the product — we find it at the factory', 'Saw a product in a shop or an ad? Upload its photo'],
    svcHead: 'Can\'t find your product? Three services bring it to you',
    tiles: { rfq: ['Request a quote', 'Factories price it for you within 48 hours'], managed: ['Managed order', 'Our team does everything for you'], idea: ['Make your idea', 'A product with your name and brand'] },
    banner: { badge: 'The price you see is the price you pay', title: 'Factory goods at wholesale prices, and the final price with shipping and customs shows in your cart before you pay',
      text: 'Thousands of products at factory prices, shipping and customs included, no surprises', cta: 'Browse products' },
    trust: ['Factory prices, direct', 'The final price with shipping and customs, in your cart before you pay', 'Secure payment and order tracking'],
    bottomHead: 'Can\'t find your product? We bring it from the factory',
    cards: {
      rfq: { title: 'Request a quote', tag: 'Free · factory offers within 48 hours', text: 'Write what you want — factories send their offers and you pick the best', note: 'For you if: you know your product and want the best price' },
      managed: { title: 'Managed order', tag: 'The easiest if you are importing for the first time', text: 'Our team handles everything: finds the factory, negotiates, inspects the goods and ships them to you', note: 'For you if: it is your first import, or you have no time' },
      idea: { title: 'Make your idea', tag: 'A product with your name and brand', text: 'Have a product idea, or want your logo on it? We find the factory that makes it', note: 'For you if: you want to build your own brand' },
    },
    seeAll: 'See all', closed: 'The market is not open yet.', previewDenied: 'Your account is an admin, but the marketplace database this app talks to does not know it as one, so it returns no unpublished products.',
  },
};
HOME_T.zh = HOME_T.en;

/** A shuffled copy. */
export function shuffled(list) {
  const a = Array.isArray(list) ? [...list] : [];
  for (let i = a.length - 1; i > 0; i--) { const j = Math.floor(Math.random() * (i + 1)); [a[i], a[j]] = [a[j], a[i]]; }
  return a;
}

export function CardRow({ rows, lang, user, exchangeRates, max = 10, cols, strip = false }) {
  return (
    <div className={`mk-grid${cols === 4 ? ' cols-4' : ''}${strip ? ' mk-strip' : ''}`}>
      {rows.slice(0, max).map((p) => <MarketProductCard key={p.id} p={p} lang={lang} user={user} exchangeRates={exchangeRates} />)}
    </div>
  );
}

function Section({ id, title, sub, onAll, allLabel, children }) {
  return (
    <section className="mk-sec" data-market-section={id}>
      <div className="mk-sec-head">
        <div><h2>{title}</h2>{sub && <p>{sub}</p>}</div>
        {onAll && <button type="button" className="mk-more" onClick={onAll}>{allLabel}</button>}
      </div>
      {children}
    </section>
  );
}

/** The managed order, one card under the hero: the first thing after the products that says MAABAR can do it for you. */
export function ManagedCard({ lang }) {
  const nav = useNavigate();
  const c = HOME_T[lang] || HOME_T.ar;
  return (
    <button type="button" className="mk-managed-card" data-managed-card="1" onClick={() => SERVICE_ROUTES.managed(nav)}>
      <span><b>{c.hire[0]}</b><span>{c.hire[1]}</span></span>
      <span className="mk-managed-go" aria-hidden="true">{lang === 'ar' ? '←' : '→'}</span>
    </button>
  );
}

/** «حصريات معبر»: one card to its own page, with the managed card's arrow (owner, 2026-10-29: no photos). Shown only when there are any. */
export function ExclusivesEntry({ lang, rows }) {
  const nav = useNavigate();
  const c = HOME_T[lang] || HOME_T.ar;
  // Customers get no rows while the section is hidden (server); the team gets
  // them, and the card says it is hidden.
  const [pub, setPub] = useState(true);
  useEffect(() => { let alive = true; exclusivesPublic().then((v) => { if (alive) setPub(v); }); return () => { alive = false; }; }, []);
  if (!rows?.length) return null;
  return (
    <button type="button" className="mk-excl-entry" data-exclusives-entry={rows.length} onClick={() => nav('/market/exclusives')}>
      <span className="mk-excl-entry-copy"><b>{c.excl[0]}{!pub && <small className="mk-excl-hidden-tag" data-exclusives-hidden-tag="1"> · {c.exclHidden}</small>}</b><span>{c.excl[1]}</span></span>
      <span className="mk-managed-go" aria-hidden="true">{lang === 'ar' ? '←' : '→'}</span>
    </button>
  );
}

/** One slim row (a sidebar list where a page has a sidebar). */
export function ServicesPanel({ lang, title, vertical = false }) {
  const nav = useNavigate();
  const c = HOME_T[lang] || HOME_T.ar;
  return (
    <aside className={`mk-svc-strip${vertical ? ' vertical' : ''}`} data-sourcing-services="1" aria-label={c.services}>
      <span className="mk-svc-title">{title || c.services}</span>
      {['managed', 'rfq', 'idea'].map((k) => (
        <button key={k} type="button" className="mk-svc" data-service-link={k} onClick={() => SERVICE_ROUTES[k](nav)}>
          <b>{c[k][0]}</b><span>{c[k][1]}</span>
        </button>
      ))}
    </aside>
  );
}

/** The largest listed subcategories across the market, as image tiles. */
export function PopularSubcategories({ show, lang, max = 12 }) {
  const nav = useNavigate();
  const c = HOME_T[lang] || HOME_T.ar;
  const subs = MARKET_CATEGORY_TREE.flatMap((cat) => cat.groups.filter((g) => show?.[cat.code]?.leaves[g.code]?.count > 0)
    .map((g) => ({ cat: cat.code, g, ...show[cat.code].leaves[g.code] })))
    .sort((a, b) => b.count - a.count).slice(0, max);
  return (
    <div className="mk-leaf-grid mk-popular" data-popular-subcategories={subs.length}>
      {subs.map((x) => (
        <button key={x.g.code} type="button" className="mk-leaf" data-popular={x.g.code} onClick={() => nav(`/market/c/${x.cat}/${x.g.code}`)}>
          <Thumb refId={x.images?.[0]} /><span><b>{lang === 'ar' ? x.g.ar : x.g.en}</b>{SHOW_CATEGORY_COUNTS && <span className="mk-count">{c.products(x.count)}</span>}</span>
        </button>
      ))}
    </div>
  );
}

export function CategoryCards({ show, lang, max = 12 }) {
  const nav = useNavigate();
  const c = HOME_T[lang] || HOME_T.ar;
  const cats = MARKET_CATEGORY_TREE.filter((x) => show?.[x.code]?.total > 0).sort((a, b) => show[b.code].total - show[a.code].total).slice(0, max);
  return (
    <div className="mk-catcards" data-category-grid={cats.length}>
      {cats.map((x) => {
        const imgs = show[x.code].images || [];
        return (
          <button key={x.code} type="button" className="mk-catcard" data-category={x.code} onClick={() => nav(`/market/c/${x.code}`)}>
            <div className={`mk-catcard-imgs${imgs.length < 4 ? ' one' : ''}`}>
              {(imgs.length < 4 ? imgs.slice(0, 1) : imgs.slice(0, 4)).map((r) => <Thumb key={r} refId={r} />)}
              {!imgs.length && <span className="mk-thumb-ph" />}
            </div>
            <div className="mk-catcard-body"><b>{marketCategoryLabel(x.code, lang)}</b>{SHOW_CATEGORY_COUNTS && <span className="mk-count">{c.products(show[x.code].total)}</span>}</div>
          </button>
        );
      })}
    </div>
  );
}

/** A curated collection (market_merchandising.sql): its own title, the products, "see all". */
export function CollectionSection({ x, lang, user, exchangeRates }) {
  const nav = useNavigate();
  const c = HOME_T[lang] || HOME_T.ar;
  const t = (k) => (lang === 'ar' ? x[`${k}_ar`] : x[`${k}_en`]);
  return (
    <Section id={`col-${x.slug}`} title={t('title')} sub={t('subtitle')} onAll={() => nav(`/market/collection/${x.slug}`)} allLabel={c.seeAll}>
      <CardRow rows={x.rows} lang={lang} user={user} exchangeRates={exchangeRates} strip />
    </Section>
  );
}


const SVC_ICON = {
  rfq: <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" aria-hidden="true"><path d="M6 3h8l4 4v14H6z" /><path d="M14 3v4h4M9 12h6M9 16h6" /></svg>,
  managed: <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true"><circle cx="12" cy="8" r="4" /><path d="M4 21c1.5-4 4.5-6 8-6s6.5 2 8 6" /></svg>,
  idea: <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" aria-hidden="true"><path d="M9 18h6M10 21h4M12 3a6 6 0 0 0-3.5 10.9c.6.5 1 1.2 1 2.1h5c0-.9.4-1.6 1-2.1A6 6 0 0 0 12 3z" /></svg>,
};
const SVC_ORDER = ['rfq', 'managed', 'idea'];

/** The photo search as a clear card under the search bar. */
export function PhotoSearchCard({ lang }) {
  const nav = useNavigate();
  const c = HOME_T[lang] || HOME_T.ar;
  const sc = SHELL_T[lang] || SHELL_T.ar;
  const ref = useRef(null);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  if (!IMAGE_SEARCH) return null;
  const pick = async (file) => {
    setErr(''); setBusy(true);
    const prep = await prepareSearchImage(file);
    setBusy(false);
    if (ref.current) ref.current.value = '';
    if (prep.error) { setErr(sc.imgErr[prep.error] || sc.imgErr.unreadable); return; }
    if (stashSearchImage(prep)) nav('/market/image-search');
  };
  return (
    <>
      <button type="button" className="mk-photo-card" data-home-photo-search="1" disabled={busy} onClick={() => ref.current?.click()}>
        <span className="mk-photo-icon" aria-hidden="true">
          <svg viewBox="0 0 24 24" width="26" height="26" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round"><path d="M4 8h3l1.6-2.4h6.8L17 8h3v11H4z" /><circle cx="12" cy="13.2" r="3.6" /></svg>
        </span>
        <span><b>{c.photo[0]}</b><span>{c.photo[1]}</span></span>
      </button>
      <input ref={ref} type="file" accept={ACCEPTED.join(',')} hidden onChange={(e) => e.target.files?.[0] && pick(e.target.files[0])} />
      {err && <p className="mk-cam-err" role="alert">{err}</p>}
    </>
  );
}

/** «ما لقيت منتجك؟ عندنا 3 خدمات تجيبه لك» — three coloured tiles side by side. */
export function ServiceTiles({ lang }) {
  const nav = useNavigate();
  const c = HOME_T[lang] || HOME_T.ar;
  return (
    <section className="mk-svc-tiles-wrap" data-service-tiles="1">
      <h2 className="mk-home-h2">{c.svcHead}</h2>
      <div className="mk-svc-tiles">
        {SVC_ORDER.map((k) => (
          <button key={k} type="button" className={`mk-svc-tile ${k}`} data-service-link={k} onClick={() => SERVICE_ROUTES[k](nav)}>
            <span className="mk-svc-tile-icon">{SVC_ICON[k]}</span>
            <b>{c.tiles[k][0]}</b><span>{c.tiles[k][1]}</span>
          </button>
        ))}
      </div>
    </section>
  );
}

export function HomeBanner({ lang }) {
  const nav = useNavigate();
  const b = (HOME_T[lang] || HOME_T.ar).banner;
  return (
    <section className="mk-home-banner" data-home-banner="1">
      <span className="mk-home-banner-badge">{b.badge}</span>
      <h1>{b.title}</h1>
      <p>{b.text}</p>
      <button type="button" className="mk-btn" onClick={() => nav('/market/products')}>{b.cta}</button>
    </section>
  );
}

const TRUST_ICON = [
  <svg key="f" viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" aria-hidden="true"><path d="M3 21V10l5 3V10l5 3V6l8 4v11z" /></svg>,
  <svg key="d" viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" aria-hidden="true"><path d="M3 7h11v9H3zM14 10h4l3 3v3h-7" /><circle cx="7" cy="18" r="1.6" /><circle cx="17" cy="18" r="1.6" /></svg>,
  <svg key="s" viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" aria-hidden="true"><path d="M12 3l8 3v6c0 4.5-3.4 8-8 9-4.6-1-8-4.5-8-9V6z" /><path d="m9 12 2 2 4-4" /></svg>,
];
export function TrustTiles({ lang }) {
  const c = HOME_T[lang] || HOME_T.ar;
  return (
    <div className="mk-trust-tiles" data-trust-tiles="1">
      {c.trust.map((t, i) => <div key={t} className="mk-trust-tile"><span aria-hidden="true">{TRUST_ICON[i]}</span><b>{t}</b></div>)}
    </div>
  );
}

// One icon per top-level category — no product photos, so the grid is never empty.
const CAT_ICON = {
  consumer_electronics: '🎧', phones_accessories: '📱', computers_office_electronics: '💻', electronic_components: '🔌', home_appliances: '🧺',
  apparel: '👕', shoes_bags_accessories: '👜', jewelry_watches: '⌚', beauty_personal_care: '💄', health_medical: '🩺', home_kitchen: '🍳',
  furniture: '🛋️', home_decor_textiles: '🛏️', garden_outdoor: '🌿', toys_kids_baby: '🧸', sports_entertainment: '⚽', automotive: '🚗',
  machinery: '⚙️', tools_hardware: '🔧', construction_building: '🧱', packaging_printing: '📦', office_school: '✏️', commercial_restaurant: '🍽️',
  electrical_energy: '💡', security_safety: '🛡️', agriculture_food: '🌾', raw_materials: '🪵', gifts_crafts: '🎁', pet_supplies: '🐾',
};
export function CategoryIconGrid({ show, lang }) {
  const nav = useNavigate();
  const cats = MARKET_CATEGORY_TREE.filter((x) => !show || show[x.code]?.total > 0)
    .sort((a, b) => (show ? show[b.code].total - show[a.code].total : 0));
  return (
    <div className="mk-cat-icons" data-category-grid={cats.length}>
      {cats.map((x) => (
        <button key={x.code} type="button" className="mk-cat-icon" data-category={x.code} onClick={() => nav(`/market/c/${x.code}`)}>
          <span className="mk-cat-icon-glyph" aria-hidden="true">{CAT_ICON[x.code] || '📦'}</span>
          <span>{marketCategoryLabel(x.code, lang)}</span>
        </button>
      ))}
    </div>
  );
}

/** «ما لقيت منتجك؟ نجيبه لك من المصنع» — the three services as full cards, at the bottom. */
export function ServiceCards({ lang }) {
  const nav = useNavigate();
  const c = HOME_T[lang] || HOME_T.ar;
  return (
    <section className="mk-svc-cards-wrap" data-service-cards="1">
      <h2 className="mk-home-h2">{c.bottomHead}</h2>
      <div className="mk-svc-cards">
        {SVC_ORDER.map((k) => {
          const x = c.cards[k];
          return (
            <button key={k} type="button" className={`mk-svc-card ${k}`} data-service-card={k} onClick={() => SERVICE_ROUTES[k](nav)}>
              <span className="mk-svc-card-icon">{SVC_ICON[k]}</span>
              <b className="mk-svc-card-title">{x.title}</b>
              <span className="mk-svc-card-tag">{x.tag}</span>
              <span className="mk-svc-card-text">{x.text}</span>
              <span className="mk-svc-card-note">{x.note}</span>
            </button>
          );
        })}
      </div>
    </section>
  );
}

// ── The redesigned home (owner, 2026-11-02 — web clarity redesign) ───────────
// Top to bottom: dark hero with the search inside it · «كيف يشتغل؟» ·
// «اختيارات التجار» (hand-picked, by store type) · the three services as a
// compact list · «شاهدتها مؤخرًا» when there is a history.
// Not on the home any more (components kept): «الأقسام» (8 tiles — owner,
// 2026-11-02: the picks tabs, the header button and the bottom bar reach every
// category already), the photo-search card, the
// service tiles, the banner, «حصريات معبر», «مقترحات لك» and every automatic
// collection. Products whose shipping staff price («اطلب تثبيت سعر الشحن»)
// never show here.

export const HOME2_T = {
  ar: {
    pill: 'السعر اللي تشوفه هو اللي تدفعه', h1: 'بضاعة المصانع بسعر الجملة، والسعر النهائي مع الشحن والجمارك يطلع لك في السلة قبل الدفع',
    sub: 'السعر النهائي شامل الشحن والجمارك يظهر في سلتك قبل الدفع. ولا ريال زيادة عند الاستلام.', search: 'ابحث باسم المنتج أو صوّره',
    trust: ['توصلك خلال 15–25 يوم', 'السعر النهائي مع الشحن والجمارك في سلتك قبل الدفع', 'دفع آمن ومتابعة لطلبك'],
    howHead: 'كيف يشتغل؟', how: ['اختر منتجك من المصنع', 'شوف السعر النهائي في السلة وادفع', 'تستلمها عندك'],
    picksHead: 'اختيارات التجار', seeAll: 'عرض الكل', catsHead: 'الأقسام', allCats: 'كل الأقسام',
    svcHead: 'ما لقيت منتجك؟ فريقنا يجيبه لك من المصنع', recent: 'شاهدتها مؤخرًا',
  },
  en: {
    pill: 'The price you see is the price you pay', h1: 'Factory goods at wholesale prices, and the final price with shipping and customs shows in your cart before you pay',
    sub: 'Your final price, shipping and customs included, shows in your cart before you pay. Not a riyal more on delivery.', search: 'Search by product name or a photo',
    trust: ['Delivered in 15–25 days', 'The final price with shipping and customs, in your cart before you pay', 'Secure payment, order tracking'],
    howHead: 'How it works', how: ['Pick your product from the factory', 'See the final price in your cart and pay', 'Receive it at your door'],
    picksHead: 'Traders\' picks', seeAll: 'See all', catsHead: 'Categories', allCats: 'All categories',
    svcHead: 'Can\'t find your product? Our team brings it from the factory', recent: 'Recently viewed',
  },
};
HOME2_T.zh = HOME2_T.en;

/** home_section_viewed once, when half a section is on screen (Growth OS). */
export function useSectionSeen(id, position) {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el || typeof IntersectionObserver === 'undefined') return undefined;
    let done = false;
    const io = new IntersectionObserver((es) => {
      if (done || !es.some((e) => e.isIntersecting && e.intersectionRatio >= 0.5)) return;
      done = true; io.disconnect();
      track('home_section_viewed', { section_id: id, position });
    }, { threshold: [0.5] });
    io.observe(el);
    return () => io.disconnect();
  }, [id, position]);
  return ref;
}

function Hero({ lang }) {
  const c = HOME2_T[lang] || HOME2_T.ar;
  const sc = SHELL_T[lang] || SHELL_T.ar;
  const ref = useSectionSeen('hero', 1);
  return (
    <section className="mk-hero2" data-home-hero="1" data-growth-section="hero" ref={ref}>
      <span className="mk-hero2-pill">{c.pill}</span>
      <h1>{c.h1}</h1>
      <p>{c.sub}</p>
      <div className="mk-hero2-search"><SearchBox lang={lang} c={sc} placeholder={c.search} /></div>
      <ul className="mk-hero2-trust">{c.trust.map((t) => <li key={t}>{t}</li>)}</ul>
    </section>
  );
}

function HowItWorks({ lang }) {
  const c = HOME2_T[lang] || HOME2_T.ar;
  const ref = useSectionSeen('how_it_works', 2);
  return (
    <section className="mk-how" data-home-how="1" ref={ref}>
      <h2 className="mk-home-h2">{c.howHead}</h2>
      <ol className="mk-how-cards">
        {c.how.map((t, i) => <li key={t}><span className="mk-how-n">{i + 1}</span><b>{t}</b></li>)}
      </ol>
    </section>
  );
}

// One row of tabs, scrolling sideways on every screen, with an arrow on each
// side on a wide screen (owner, 2026-11-02) — visual left/right, whatever the
// language direction.
function TabRow({ children, lang }) {
  const row = useRef(null);
  const [edge, setEdge] = useState({ left: true, right: true });
  const measure = () => {
    const el = row.current; if (!el) return;
    const max = el.scrollWidth - el.clientWidth;
    const rtl = getComputedStyle(el).direction === 'rtl';
    const fromStart = rtl ? -el.scrollLeft : el.scrollLeft;   // Chrome/Safari: 0 … -max in RTL
    const atStart = fromStart <= 1; const atEnd = fromStart >= max - 1;
    const next = { left: rtl ? atEnd : atStart, right: rtl ? atStart : atEnd };
    setEdge((x) => (x.left === next.left && x.right === next.right ? x : next));
  };
  useEffect(() => { measure(); window.addEventListener('resize', measure); return () => window.removeEventListener('resize', measure); });
  const go = (dir) => { const el = row.current; if (el) el.scrollBy({ left: dir * el.clientWidth * 0.8, behavior: 'smooth' }); };
  return (
    <div className="mk-picks-tabrow">
      <button type="button" className="mk-picks-arrow l" aria-label="‹" hidden={edge.left} onClick={() => go(-1)} data-picks-arrow="left">‹</button>
      <div className="mk-picks-tabs" role="tablist" ref={row} onScroll={measure} dir={lang === 'ar' ? 'rtl' : 'ltr'}>{children}</div>
      <button type="button" className="mk-picks-arrow r" aria-label="›" hidden={edge.right} onClick={() => go(1)} data-picks-arrow="right">›</button>
    </div>
  );
}

function TraderPicks({ lang, user, exchangeRates }) {
  const nav = useNavigate();
  const c = HOME2_T[lang] || HOME2_T.ar;
  const dcfg = useDeliveredConfig();
  const ref = useSectionSeen('trader_picks', 3);
  const [tabs, setTabs] = useState(null);   // «منوّع» first, then the categories (dayTabs)
  const [rows, setRows] = useState({});     // key → products, filled as tabs are opened
  const [cur, setCur] = useState(MIX);
  // the page loads «منوّع» only (owner, 2026-11-02); the tab names come from the category counts
  useEffect(() => {
    if (!dcfg) return undefined;
    let alive = true;
    Promise.all([dayTabs(), mixPicks({ dcfg, rates: exchangeRates })]).then(([list, mix]) => {
      if (!alive) return;
      if (mix.length < PICKS_MIN) { setTabs([]); return; }
      setTabs(list); setRows({ [MIX]: mix }); setCur(MIX);
    }).catch(() => { if (alive) setTabs([]); });
    return () => { alive = false; };
  }, [dcfg, exchangeRates]);
  // a category's products when its tab is opened; fewer than PICKS_MIN eligible: the tab goes
  useEffect(() => {
    if (!dcfg || cur === MIX || rows[cur]) return undefined;
    let alive = true;
    categoryPicks(cur, { dcfg, rates: exchangeRates }).then((list) => {
      if (!alive) return;
      if (list.length < PICKS_MIN) { setTabs((t) => t.filter((x) => x.key !== cur)); setCur(MIX); return; }
      setRows((r) => ({ ...r, [cur]: list }));
    }).catch(() => { if (alive) setCur(MIX); });
    return () => { alive = false; };
  }, [cur, dcfg, exchangeRates, rows]);
  if (!tabs || !tabs.length) return <div ref={ref} />;
  const shown = rows[cur];
  return (
    <section className="mk-sec mk-picks" data-market-section="trader-picks" data-home-picks={tabs.length} ref={ref}>
      <div className="mk-sec-head">
        <div><h2>{c.picksHead}</h2></div>
        <button type="button" className="mk-more" data-picks-all={cur} onClick={() => nav(`/market/picks/${cur}`)}>{c.seeAll}</button>
      </div>
      <TabRow lang={lang}>
        {tabs.map((t, i) => (
          <button key={t.key} type="button" role="tab" aria-selected={t.key === cur} className={t.key === cur ? 'on' : ''} data-picks-tab={t.key}
            onClick={() => { setCur(t.key); track('home_item_clicked', { section_id: 'trader_picks_tab', position: i + 1, category: t.key }); }}>
            {lang === 'ar' ? t.ar : t.en}
          </button>
        ))}
      </TabRow>
      {shown ? (
        <div className="mk-grid mk-grid-2" data-picks-loaded={cur}>
          {shown.slice(0, PICKS_HOME).map((p) => <MarketProductCard key={p.id} p={p} lang={lang} user={user} exchangeRates={exchangeRates} />)}
        </div>
      ) : <div className="mk-skel" style={{ height: 320 }} data-picks-loading={cur} />}
    </section>
  );
}

// «الأقسام» on the home: the owner's eight, each mapped to the closest store category.
export const HOME_CATS = [
  { code: 'phones_accessories', ar: 'إكسسوارات الجوالات' }, { code: 'consumer_electronics', ar: 'الإلكترونيات' },
  { code: 'computers_office_electronics', ar: 'الكمبيوتر والمكتب' }, { code: 'jewelry_watches', ar: 'المجوهرات والساعات' },
  { code: 'home_kitchen', ar: 'المنزل والمطبخ' }, { code: 'automotive', ar: 'السيارات' },
  { code: 'tools_hardware', ar: 'العدد والأدوات' }, { code: 'packaging_printing', ar: 'التغليف والطباعة' },
];

// not rendered on the home since 2026-11-02 (kept, as the header comment above says)
export function HomeCategories({ show, lang }) {
  const nav = useNavigate();
  const c = HOME2_T[lang] || HOME2_T.ar;
  const ref = useSectionSeen('categories', 4);
  // the owner's eight, in his order and words (2026-11-02); one without products is left out
  const cats = HOME_CATS.filter((x) => !show || show[x.code]?.total > 0);
  return (
    <section className="mk-sec" data-market-section="categories" ref={ref}>
      <div className="mk-sec-head">
        <div><h2>{c.catsHead}</h2></div>
        <button type="button" className="mk-more" data-all-categories="1" onClick={() => nav('/market/products')}>{c.allCats}</button>
      </div>
      <div className="mk-cat-icons mk-cat-8" data-category-grid={cats.length}>
        {cats.map((x) => (
          <button key={x.code} type="button" className="mk-cat-icon" data-category={x.code} onClick={() => nav(`/market/c/${x.code}`)}>
            <span className="mk-cat-icon-glyph" aria-hidden="true">{CAT_ICON[x.code] || '📦'}</span>
            <span>{lang === 'ar' ? x.ar : marketCategoryLabel(x.code, lang)}</span>
          </button>
        ))}
      </div>
    </section>
  );
}

function ServiceList({ lang }) {
  const nav = useNavigate();
  const c = HOME2_T[lang] || HOME2_T.ar;
  const t = (HOME_T[lang] || HOME_T.ar).tiles;
  const ref = useSectionSeen('services', 4);
  return (
    <section className="mk-svc-list" data-service-list="1" ref={ref}>
      <h2 className="mk-home-h2">{c.svcHead}</h2>
      {SVC_ORDER.map((k) => (
        <button key={k} type="button" className="mk-svc-row" data-service-link={k} onClick={() => SERVICE_ROUTES[k](nav)}>
          <span className="mk-svc-tile-icon">{SVC_ICON[k]}</span>
          <span><b>{t[k][0]}</b><span>{t[k][1]}</span></span>
          <svg className="mk-svc-chev" viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true"><path d={lang === 'ar' ? 'm15 6-6 6 6 6' : 'm9 6 6 6-6 6'} /></svg>
        </button>
      ))}
    </section>
  );
}

export default function MarketHome({ lang = 'ar', user, profile, exchangeRates }) {
  const c = HOME_T[lang] || HOME_T.ar;
  const h = HOME2_T[lang] || HOME2_T.ar;
  useEffect(() => { document.title = 'مَعبر | بضاعة المصانع واصلة لك'; }, []);
  const isAdmin = ['admin', 'super_admin'].includes(String(profile?.role || '').toLowerCase());
  const [state, setState] = useState(null);
  const [show, setShow] = useState(null);
  const [d, setD] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      const [st, sc] = await Promise.all([fetchMarketState({ refresh: true }), fetchShowcase({ refresh: true })]);
      if (!alive) return;
      setState(st); setShow(sc);
      const recent = recentIds();
      const [all, rec] = await Promise.all([searchMarket({ limit: 1 }), fetchMarketCards(recent.slice(0, 10))]);
      if (alive) setD({ recent: rec.rows, total: all.total });
    })();
    return () => { alive = false; };
  }, [user?.id]);

  const loading = !d || !show;
  const empty = !loading && d.total === 0;
  return (
    <MarketShell lang={lang} user={user} hideSearch>
      <div className="mk-wrap mk-home2" style={{ paddingBottom: 48 }}>
        {loading ? (
          <div style={{ marginTop: 14 }}><div className="mk-skel" style={{ height: 300, borderRadius: 20 }} /><div className="mk-skel" style={{ height: 160, marginTop: 14 }} /></div>
        ) : empty ? (
          <div style={{ marginTop: 24 }}>
            <p className="mk-empty" data-market-closed="1">{c.closed}</p>
            {isAdmin && !state?.preview && !state?.marketplace_public && <p className="mk-count" data-preview-denied="1" style={{ textAlign: 'center' }}>{c.previewDenied}</p>}
            <ServicesPanel lang={lang} title={c.notFound} />
          </div>
        ) : (
          <>
            <Hero lang={lang} />
            <HowItWorks lang={lang} />
            <TraderPicks lang={lang} user={user} exchangeRates={exchangeRates} />
            <ServiceList lang={lang} />
            {d.recent.length > 0 && <Section id="recent" title={h.recent}><CardRow rows={d.recent} lang={lang} user={user} exchangeRates={exchangeRates} /></Section>}
          </>
        )}
      </div>
    </MarketShell>
  );
}
